import React from "react";
import {useIntl} from "react-intl";
import {useRestaurant} from "@/context/restaurant/RestaurantProvider";
import {useMutation} from "@tanstack/react-query";
import {FulfillmentStatus, RestaurantLocation} from "@/data/entities/restaurant";
import {notifyDanger, notifySuccess} from "@/utils/NotifyUtils";
import {Alert, useNotifications} from "peggirkit";
import ToggleButton from "@widgets/busyness/editor/ToggleButton";
import {api} from "@/data/api";

const isSelected = (value: FulfillmentStatus, isDelivery: boolean, location: RestaurantLocation): boolean =>
    isDelivery
        ? (location.statusDelivery === value)
        : (location.statusTakeaway === value)

type Props = {
    isDelivery: boolean,
};

const FulfillmentStatusUpdater = ({isDelivery}: Props) => {
    const intl = useIntl();
    const {dashboardLocation, setDashboardLocation, setLocations} = useRestaurant();
    const {notify} = useNotifications();
    const {mutate, isPending} = useMutation({
        mutationFn: async (newStatus: FulfillmentStatus): Promise<RestaurantLocation[]> =>
            api.restaurant.updateFulfillmentStatus(dashboardLocation.id, {
                statusDelivery: isDelivery ? newStatus : null,
                statusTakeaway: isDelivery ? null : newStatus,
            }),
        onSuccess: (updatedLocations: RestaurantLocation[]) => {
            const selectedLocation = updatedLocations.filter(x => x.id === dashboardLocation.id)[0]
            setLocations(updatedLocations);
            setDashboardLocation(selectedLocation);
            notifySuccess(intl.formatMessage({id: "saved"}), notify);
        },
        onError: () => notifyDanger(intl.formatMessage({id: "systemError"}), notify),
    });
    const isDefault = isSelected("DEFAULT", isDelivery, dashboardLocation)
    const isBusy = isSelected("BUSY", isDelivery, dashboardLocation)
    const isClosed = isSelected("CLOSED", isDelivery, dashboardLocation)

    return (
        <div className={"w-full grid md:grid-cols-3 gap-5"}>
            {(isBusy || isClosed) &&
                <Alert
                    containerClassname={"md:col-span-3"}
                    type={isBusy ? "warning" : "danger"}
                    title={intl.formatMessage({id: "manuallyRevert"})}
                    content={intl.formatMessage({id: "manuallyRevertStatusExplanation"})}
                />
            }

            <ToggleButton
                status={"DEFAULT"}
                selected={isDefault}
                disabled={isPending}
                title={intl.formatMessage({id: "normal"})}
                description={intl.formatMessage({id: "fulfillmentStatusNormalExplanation"})}
                onClick={() => mutate("DEFAULT")}
            />

            <ToggleButton
                status={"BUSY"}
                selected={isBusy}
                disabled={isPending}
                title={intl.formatMessage({id: "busy"})}
                description={intl.formatMessage({id: "fulfillmentStatusBusyExplanation"})}
                onClick={() => mutate("BUSY")}
            />

            <ToggleButton
                status={"CLOSED"}
                selected={isClosed}
                disabled={isPending}
                title={intl.formatMessage({id: "closed"})}
                description={intl.formatMessage({id: "fulfillmentStatusClosedExplanation"})}
                onClick={() => mutate("CLOSED")}
            />
        </div>
    );
};

export default FulfillmentStatusUpdater;